import React, { useEffect, useState } from 'react';
import {
    Alert,
    alpha,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme as useMuiTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    configurationsService,
    DisciplineConfig,
    DocumentTypeConfig,
} from '../services/configurationsService';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import { useFormFieldErrors, REQUIRED_FIELDS_MESSAGE } from '../hooks/useFormFieldErrors';
import FormValidationAlert from '../components/FormValidationAlert';

interface RevisionConfig {
    id: number;
    name: string;
    abbreviation_meaning: string;
}

type EntityKind = 'revision' | 'documentType' | 'discipline';

const RevisionsConfigPage: React.FC = () => {
    const theme = useMuiTheme();
    const { mode } = useThemeContext();

    const consoleColors = {
        deepOcean: '#0f2134',
        abyssal: '#0a1622',
        sand: '#ccba9b',
        text: '#ffffff',
        textDim: 'rgba(255, 255, 255, 0.7)',
    };

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [revisions, setRevisions] = useState<RevisionConfig[]>([]);
    const [documentTypes, setDocumentTypes] = useState<DocumentTypeConfig[]>([]);
    const [disciplines, setDisciplines] = useState<DisciplineConfig[]>([]);

    const [open, setOpen] = useState(false);
    const [entityKind, setEntityKind] = useState<EntityKind>('revision');
    const [editingId, setEditingId] = useState<number | null>(null);

    const [revisionForm, setRevisionForm] = useState({ name: '', abbreviation_meaning: '' });
    const [documentTypeForm, setDocumentTypeForm] = useState({ name: '', description: '' });
    const [disciplineForm, setDisciplineForm] = useState({ discipline: '', description: '' });
    const fieldErrors = useFormFieldErrors();

    const loadAll = async () => {
        try {
            setLoading(true);
            setError(null);
            const [revisionsData, documentTypesData, disciplinesData] = await Promise.all([
                configurationsService.getRevisions(),
                configurationsService.getDocumentTypes(),
                configurationsService.getDisciplines(),
            ]);
            setRevisions(revisionsData);
            setDocumentTypes(documentTypesData);
            setDisciplines(disciplinesData);
        } catch (err: any) {
            setError(err.message || 'Failed to load configuration parameters');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const panelBorder = `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`;
    const rowBorder = `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}`;

    const openCreateDialog = (kind: EntityKind) => {
        setEntityKind(kind);
        setEditingId(null);
        setRevisionForm({ name: '', abbreviation_meaning: '' });
        setDocumentTypeForm({ name: '', description: '' });
        setDisciplineForm({ discipline: '', description: '' });
        fieldErrors.clear();
        setOpen(true);
    };

    const openEditDialog = (kind: EntityKind, row: RevisionConfig | DocumentTypeConfig | DisciplineConfig) => {
        setEntityKind(kind);
        setEditingId(row.id);

        if (kind === 'revision') {
            const revision = row as RevisionConfig;
            setRevisionForm({
                name: revision.name || '',
                abbreviation_meaning: revision.abbreviation_meaning || '',
            });
        }

        if (kind === 'documentType') {
            const documentType = row as DocumentTypeConfig;
            setDocumentTypeForm({
                name: documentType.name || '',
                description: documentType.description || '',
            });
        }

        if (kind === 'discipline') {
            const discipline = row as DisciplineConfig;
            setDisciplineForm({
                discipline: discipline.discipline || '',
                description: discipline.description || '',
            });
        }

        fieldErrors.clear();
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingId(null);
        fieldErrors.clear();
    };

    const handleSave = async () => {
        if (entityKind === 'revision' && !revisionForm.name.trim()) {
            fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['name']);
            return;
        }
        if (entityKind === 'documentType' && !documentTypeForm.name.trim()) {
            fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['name']);
            return;
        }
        if (entityKind === 'discipline' && !disciplineForm.discipline.trim()) {
            fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['discipline']);
            return;
        }
        try {
            if (entityKind === 'revision') {
                if (editingId) {
                    await configurationsService.updateRevision(editingId, revisionForm);
                } else {
                    await configurationsService.addRevision(revisionForm);
                }
            }

            if (entityKind === 'documentType') {
                if (editingId) {
                    await configurationsService.updateDocumentType(editingId, documentTypeForm);
                } else {
                    await configurationsService.addDocumentType(documentTypeForm);
                }
            }

            if (entityKind === 'discipline') {
                if (editingId) {
                    await configurationsService.updateDiscipline(editingId, disciplineForm);
                } else {
                    await configurationsService.addDiscipline(disciplineForm);
                }
            }

            handleClose();
            await loadAll();
        } catch (err: any) {
            fieldErrors.show(err.message || 'Failed to save parameter');
        }
    };

    const handleDelete = async (kind: EntityKind, id: number) => {
        const entityName = kind === 'revision' ? 'revision' : kind === 'documentType' ? 'document type' : 'discipline';
        if (!window.confirm(`Are you sure you want to delete this ${entityName}?`)) {
            return;
        }

        try {
            if (kind === 'revision') {
                await configurationsService.deleteRevision(id);
            }
            if (kind === 'documentType') {
                await configurationsService.deleteDocumentType(id);
            }
            if (kind === 'discipline') {
                await configurationsService.deleteDiscipline(id);
            }
            await loadAll();
        } catch (err: any) {
            alert(err.message || `Failed to delete ${entityName}`);
        }
    };

    const renderSectionHeader = (title: string, subtitle: string, addLabel: string, onAdd: () => void) => (
        <Box sx={{ p: 2.5, borderBottom: panelBorder, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box>
                <Typography variant="overline" sx={{ letterSpacing: '0.12em', fontWeight: 700, color: mode === 'dark' ? consoleColors.sand : 'primary.main' }}>
                    {subtitle}
                </Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700, color: mode === 'dark' ? consoleColors.text : 'text.primary' }}>
                    {title}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAdd}
                sx={{
                    borderRadius: 0,
                    border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
                    color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
                    fontFamily: 'monospace',
                    '&:hover': {
                        backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.08) : alpha(theme.palette.primary.main, 0.05),
                    },
                }}
            >
                {addLabel}
            </Button>
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{
                mb: 3,
                p: 3,
                backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
                border: panelBorder,
                borderLeft: `4px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
            }}>
                <SettingsIcon sx={{ color: mode === 'dark' ? consoleColors.sand : 'primary.main' }} />
                <Box>
                    <Typography variant="overline" sx={{ letterSpacing: '0.18em', fontWeight: 700, color: mode === 'dark' ? consoleColors.sand : 'primary.main' }}>
                        CONFIG DECK
                    </Typography>
                    <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        DOCUMENT PARAMETERS
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 0 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'grid', gap: 3 }}>
                <Paper sx={{ backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff', border: panelBorder, borderRadius: 0 }}>
                    {renderSectionHeader('Revision Names', 'REVISION BANK', 'ADD REVISION', () => openCreateDialog('revision'))}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f5f5f5' }}>
                                    <TableCell sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>DESIGNATION</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>ABBREVIATION MEANING</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>COMMANDS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {revisions.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={{ borderBottom: rowBorder, fontFamily: 'monospace', fontWeight: 600 }}>{row.name}</TableCell>
                                        <TableCell sx={{ borderBottom: rowBorder, fontFamily: 'monospace' }}>{row.abbreviation_meaning || '-'}</TableCell>
                                        <TableCell align="right" sx={{ borderBottom: rowBorder }}>
                                            <Tooltip title="MODIFY">
                                                <IconButton size="small" onClick={() => openEditDialog('revision', row)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="REMOVE">
                                                <IconButton size="small" onClick={() => handleDelete('revision', row.id)} sx={{ color: '#d32f2f' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Paper sx={{ backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff', border: panelBorder, borderRadius: 0 }}>
                    {renderSectionHeader('Document Types', 'DOCUMENT CLASS', 'ADD DOCUMENT TYPE', () => openCreateDialog('documentType'))}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f5f5f5' }}>
                                    <TableCell sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>TYPE NAME</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>DESCRIPTION</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>COMMANDS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documentTypes.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={{ borderBottom: rowBorder, fontFamily: 'monospace', fontWeight: 600 }}>{row.name}</TableCell>
                                        <TableCell sx={{ borderBottom: rowBorder, fontFamily: 'monospace' }}>{row.description || '-'}</TableCell>
                                        <TableCell align="right" sx={{ borderBottom: rowBorder }}>
                                            <Tooltip title="MODIFY">
                                                <IconButton size="small" onClick={() => openEditDialog('documentType', row)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="REMOVE">
                                                <IconButton size="small" onClick={() => handleDelete('documentType', row.id)} sx={{ color: '#d32f2f' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Paper sx={{ backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff', border: panelBorder, borderRadius: 0 }}>
                    {renderSectionHeader('Disciplines', 'DISCIPLINE MATRIX', 'ADD DISCIPLINE', () => openCreateDialog('discipline'))}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f5f5f5' }}>
                                    <TableCell sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>CODE</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>DESCRIPTION</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace', borderBottom: panelBorder }}>COMMANDS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {disciplines.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={{ borderBottom: rowBorder, fontFamily: 'monospace', fontWeight: 600 }}>{row.discipline}</TableCell>
                                        <TableCell sx={{ borderBottom: rowBorder, fontFamily: 'monospace' }}>{row.description || '-'}</TableCell>
                                        <TableCell align="right" sx={{ borderBottom: rowBorder }}>
                                            <Tooltip title="MODIFY">
                                                <IconButton size="small" onClick={() => openEditDialog('discipline', row)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="REMOVE">
                                                <IconButton size="small" onClick={() => handleDelete('discipline', row.id)} sx={{ color: '#d32f2f' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {loading && (
                <Typography sx={{ mt: 2, fontFamily: 'monospace', color: 'text.secondary' }}>
                    LOADING CONFIGURATION PARAMETERS...
                </Typography>
            )}

            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        borderRadius: 0,
                        backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
                        border: `1px solid ${mode === 'dark' ? consoleColors.sand : '#e0e0e0'}`,
                        boxShadow: `0 0 24px ${mode === 'dark' ? alpha(consoleColors.deepOcean, 0.8) : 'rgba(0,0,0,0.1)'}`,
                    },
                }}
            >
                <DialogTitle sx={{ borderBottom: panelBorder, backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f8f9fa' }}>
                    <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.9rem' }}>
                        {editingId ? 'MODIFY PARAMETERS' : 'ADD PARAMETERS'}
                    </Typography>
                </DialogTitle>
                <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
                <DialogContent sx={{ p: 3, minWidth: 420 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {entityKind === 'revision' && (
                            <>
                                <TextField
                                    label="DESIGNATION"
                                    value={revisionForm.name}
                                    error={fieldErrors.invalid('name')}
                                    onChange={(e) => {
                                        fieldErrors.clearField('name');
                                        setRevisionForm((prev) => ({ ...prev, name: e.target.value }));
                                    }}
                                    variant="filled"
                                    fullWidth
                                />
                                <TextField
                                    label="ABBREVIATION MEANING"
                                    value={revisionForm.abbreviation_meaning}
                                    onChange={(e) => setRevisionForm((prev) => ({ ...prev, abbreviation_meaning: e.target.value }))}
                                    variant="filled"
                                    fullWidth
                                />
                            </>
                        )}

                        {entityKind === 'documentType' && (
                            <>
                                <TextField
                                    label="TYPE NAME"
                                    value={documentTypeForm.name}
                                    error={fieldErrors.invalid('name')}
                                    onChange={(e) => {
                                        fieldErrors.clearField('name');
                                        setDocumentTypeForm((prev) => ({ ...prev, name: e.target.value }));
                                    }}
                                    variant="filled"
                                    fullWidth
                                />
                                <TextField
                                    label="DESCRIPTION"
                                    value={documentTypeForm.description}
                                    onChange={(e) => setDocumentTypeForm((prev) => ({ ...prev, description: e.target.value }))}
                                    variant="filled"
                                    fullWidth
                                />
                            </>
                        )}

                        {entityKind === 'discipline' && (
                            <>
                                <TextField
                                    label="DISCIPLINE CODE"
                                    value={disciplineForm.discipline}
                                    error={fieldErrors.invalid('discipline')}
                                    onChange={(e) => {
                                        fieldErrors.clearField('discipline');
                                        setDisciplineForm((prev) => ({ ...prev, discipline: e.target.value }));
                                    }}
                                    variant="filled"
                                    fullWidth
                                />
                                <TextField
                                    label="DESCRIPTION"
                                    value={disciplineForm.description}
                                    onChange={(e) => setDisciplineForm((prev) => ({ ...prev, description: e.target.value }))}
                                    variant="filled"
                                    fullWidth
                                />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, borderTop: panelBorder }}>
                    <Button onClick={handleClose} sx={{ fontFamily: 'monospace' }}>
                        CANCEL
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
                            color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
                            fontFamily: 'monospace',
                        }}
                    >
                        CONFIRM
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RevisionsConfigPage;
